import React from 'react';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { PRIME_URL } from 'constants/environments';
import useUserSettingsMutation from 'hooks/domain/user/mutations/useUserSettingsMutation';
import { PostMessageStatus, usePostMessage } from 'hooks/usePostMessage';
import { jotaiSessionStorage } from 'utils/jotai';
import { MessagesToPrime, postMessageToPrime } from 'utils/postMessageToPrime';

import { useOptInToAuthorizedLibs } from './use-add-associations';

type CompleteOnboarding = Omit<ReturnType<typeof useUserSettingsMutation>, 'mutate' | 'mutateAsync'> & {
  mutateAsync: () => Promise<void>;
};

export const onboardingJustCompletedAtom = atomWithStorage('just-completed-onboarding', false, jotaiSessionStorage);

export function useCompleteOnboarding(): CompleteOnboarding {
  const saveUserSettings = useUserSettingsMutation();
  const postMessage = usePostMessage();
  const [, setOnboardingCompletedJustNow] = useAtom(onboardingJustCompletedAtom);

  const { optInToAuthorizedLibs } = useOptInToAuthorizedLibs();

  return React.useMemo(() => {
    async function mutateAsync() {
      await optInToAuthorizedLibs();

      await saveUserSettings.mutateAsync({ isInitialized: true });
      setOnboardingCompletedJustNow(true);

      // TODO: Remove Prime specific message once 2.0 reads the new format
      postMessageToPrime(MessagesToPrime.FORMS_ONBOARDING_COMPLETE);
      postMessage({ msg: { status: PostMessageStatus.FORMS_ONBOARDING_COMPLETE }, target: PRIME_URL });
    }

    const r = { ...saveUserSettings, mutateAsync } as CompleteOnboarding;
    // @ts-expect-error
    delete r.mutate;
    return r;
  }, [saveUserSettings, setOnboardingCompletedJustNow, postMessage, optInToAuthorizedLibs]);
}
